/* eslint-disable object-curly-newline */
/* global G */
import { Grid, useTheme } from '@mui/material'
import { useCallback, useContext, useMemo } from 'react'
import ApplicationContext from '@platform/react/context/application'
import GaiaImageList from 'ui/Element/Attachment/List'
import MuiListTemplateSmall from 'ui/Element/Attachment/List/Template/Small'
import MuiListTemplateAction from 'ui/Element/Attachment/List/Template/Action'
import fileConfig from 'configPlatform/ui/_shared/file.json5'
import {
  type MessageClasses,
  type MessageVariant,
  type UploadEntry,
} from 'ui/Element/Message/List/Template/Message'

export interface BaseUploadsProps {
  /** the in-flight upload entries for the message */
  data?: UploadEntry[]
  /** message variant (drives alignment) */
  variant?: MessageVariant
}

export interface UploadsProps extends BaseUploadsProps {
  /** the parent's style classes */
  classes: MessageClasses
}

/**
 * Uploads
 *
 * Shows attachments currently being uploaded in the same fashion {@link Attachments} shows
 * remote attachments.
 *
 * @param props         the component's properties
 * @param props.data    the in-flight upload entries for the message
 * @param props.classes the parent's style classes
 * @param props.variant message variant (drives alignment)
 * @returns             the uploads, or null
 */
const Uploads = ({ data = [], classes, variant }: UploadsProps) => {
  const theme = useTheme() as any
  const { eventBus } = useContext(ApplicationContext) as any

  const onCancel = useCallback(
    (group: string, attachment: { key?: string, parent?: string }) => (
      e?: { stopPropagation?: () => void },
    ) => {
      e?.stopPropagation?.()
      eventBus.dispatch(eventBus.type(G.ATTACHMENT, G.CANCEL), {
        [G.DATA]: { key: attachment.key, parent: attachment.parent },
      })
    }, [eventBus],
  )
  const events = useMemo(() => ({ onDelete: onCancel }), [onCancel])

  const items = useMemo(() => data.map(
    entry => ({
      key: entry.key,
      parent: entry.parent,
      _rev: null,
      file: entry.url,
      upload: { progress: entry.progress },
      value: {
        name: entry.name,
        size: entry.size,
        mimetype: entry.mimetype,
        type: entry.mimetype,
      },
    }),
  ), [data])

  const placeholderProps = {
    size: '1.5rem',
    padding: '0.75rem',
    shape: 'rounded',
    ...variant === 'primary'
      ? { color: theme.palette.pending[700], background: theme.palette.pending[50] }
      : { color: theme.palette.black.main, background: theme.palette.gray[950] },
  }

  return data.length ? (
    <Grid
      item
      key={'uploads'}
      className={classes.attachments}
      sx={{ alignItems: variant === 'primary' ? 'flex-end' : 'flex-start' }}
    >
      <GaiaImageList
        data={items}
        events={events}
        download={false}
        showDelete={false}
        direction={variant === 'primary' ? 'row-reverse' : 'row'}
        types={fileConfig.types}
        filter={{ type: ['image'] }}
        ListItem={MuiListTemplateSmall}
        placeholderProps={placeholderProps}
        itemClasses={{ image: classes.attachedImage, delete: classes.uploadDelete }}
      />
      <GaiaImageList
        data={items}
        events={events}
        download={false}
        showDelete={false}
        direction={'column'}
        style={{ maxWidth: '18.75rem' }} // 300px
        types={fileConfig.types}
        filter={{ type: ['!image'] }}
        ListItem={MuiListTemplateAction}
        placeholderProps={placeholderProps}
        itemClasses={{ root: classes.genericAttachment, delete: classes.uploadDelete }}
      />
    </Grid>
  ) : null
}

export default Uploads
